import {
  APIListValuesRequest,
  APIListValuesResponse,
  APIPaginatedRequest,
  Attachment,
  CountRequest,
  DeleteRequest,
  getFetcher,
  patchFetcher,
  postFetcher,
} from "shared/api/api";
import client from "shared/api/axios";
import { Group } from "shared/api/rbac/api";
import { SmartGroup } from "shared/api/smartGroups/api";
import { createURL } from "shared/api/utils";
import { ISSUES_ROUTE } from "shared/constants";

export interface Issue {
  ID: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  assignee?: string;
  name: string;
  description?: string;
  descriptionRaw?: string;
  atRiskPopulationFilter: string;
  comparisonPopulationFilter?: string;
  claimFilter?: string;
  signalEventOccurrencesFilter?: string;
  assignedGroupID: string | null;
  assignedGroup: Group | null;
  externalID?: string;
  promotedFromID: string | null;
  smartGroupID?: string;
  smartGroup?: SmartGroup;
  attachments: Attachment[];
}

export enum PinType {
  TABLE = "table",
  TOP_CONTRIBUTORS = "topContributors",
  BY_VEHICLE_AGE = "byVehicleAge",
  RUN_CHART = "runChart",
}

export interface Pin {
  type: PinType;
  data?: any;
  ID: string;
  issueID: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePinRequest {
  data: Record<string, any>;
  type: string;
  issueID: string;
  title?: string;
}

export interface UpdatePinRequest extends CreatePinRequest {
  id: string;
  issueID: string;
  title?: string;
}

// region List Issues
export interface ListIssuesRequest extends APIPaginatedRequest {
  skipRequest?: boolean;
}

export const listIssuesRequestURI = (params: ListIssuesRequest): string =>
  params.skipRequest
    ? ""
    : client.getUri({
        method: "get",
        url: createURL([ISSUES_ROUTE]),
        params,
      });

export const countIssuesRequestURI = (params: CountRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([ISSUES_ROUTE, "count"]),
    params,
  });

// region List Issue Details
export interface GetIssueRequest {
  id: string;
  skipRequest?: boolean;
}

const getIssueRequestURI = ({ id, skipRequest }: GetIssueRequest): string =>
  skipRequest
    ? ""
    : client.getUri({
        method: "get",
        url: createURL([ISSUES_ROUTE, id]),
      });

export const getIssue = (args: GetIssueRequest) =>
  getFetcher<Issue>(getIssueRequestURI(args));
// endregion

// region Issue values
const listIssuesValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([ISSUES_ROUTE, "values", fieldName]),
    params,
  });

export const listIssuesValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listIssuesValuesRequestURI(args));
// endregion

const deleteIssueRequestURI = ({ id }: DeleteRequest): string =>
  client.getUri({
    method: "DELETE",
    url: createURL([ISSUES_ROUTE, id]),
  });

export const deleteIssue = (args: DeleteRequest) =>
  client.delete(deleteIssueRequestURI(args));

export interface NewIssueRequest {
  name: string;
  description?: string;
  assignee?: string;
  atRiskPopulationFilter: string;
  comparisonPopulationFilter?: string;
  claimFilter?: string;
  signalEventOccurrencesFilter?: string;
  statusID?: string;
  severityID?: string;
  comment?: string;
  smartGroupID?: string;
  externalID?: string | null;
}

const newIssueRequestURI = (params: NewIssueRequest): string =>
  client.getUri({
    method: "POST",
    url: ISSUES_ROUTE,
    ...params,
  });

export const newIssue = (data: NewIssueRequest) =>
  postFetcher<Issue>(newIssueRequestURI(data), data);

export interface UpdateIssueRequest extends Partial<NewIssueRequest> {
  ID: string;
}

const updateIssueRequestURI = ({ ID, ...params }: UpdateIssueRequest): string =>
  client.getUri({
    method: "PATCH",
    url: createURL([ISSUES_ROUTE, ID]),
    params,
  });

export const updateIssue = (args: UpdateIssueRequest) =>
  patchFetcher<Issue>(updateIssueRequestURI({ ID: args.ID }), args);

export interface ListPinsRequest extends APIPaginatedRequest {
  id: string;
  skipRequest?: boolean;
  refreshKey?: string;
}

export const listIssuePinsRequestURI = (params: ListPinsRequest): string =>
  params.skipRequest
    ? ""
    : client.getUri({
        method: "get",
        url: createURL([ISSUES_ROUTE, params.id, "pins"]),
        params,
      });

const updateIssuePinRequestURI = ({
  issueID,
  id,
  ...params
}: UpdatePinRequest): string =>
  client.getUri({
    method: "PATCH",
    url: createURL([ISSUES_ROUTE, issueID, "pins", id]),
    params,
  });

const createIssuePinRequestURI = ({
  issueID,
  ...params
}: CreatePinRequest): string =>
  client.getUri({
    method: "POST",
    url: createURL([ISSUES_ROUTE, issueID, "pins"]),
    params,
  });

const deleteIssuePinRequestURI = ({
  issueID,
  id,
}: {
  issueID: string;
  id: string;
}): string =>
  client.getUri({
    method: "DELETE",
    url: createURL([ISSUES_ROUTE, issueID, "pins", id]),
  });

export const createIssuePin = ({ issueID, ...args }: CreatePinRequest) =>
  postFetcher<Pin>(createIssuePinRequestURI({ issueID, ...args }), args);

export const updateIssuePin = ({ id, issueID, ...args }: UpdatePinRequest) =>
  patchFetcher<Pin>(updateIssuePinRequestURI({ id, issueID, ...args }), args);

export const deleteIssuePin = (args: { issueID: string; id: string }) =>
  client.delete(deleteIssuePinRequestURI(args));
// endregion

export const getDownloadAllIssueAttachmentsRequestURI = (id: string): string =>
  client.getUri({
    method: "GET",
    url: createURL([ISSUES_ROUTE, id, "attachments", "download"]),
  });
